import { forwardRef } from 'react';
import type { AlertDialogProps } from './AlertDialog.types';
import { Button } from '../Button/Button';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  CheckIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

/**
 * AlertDialog - Componente de alerta/diálogo del sistema de diseño Siesa
 *
 * Componente modal para confirmaciones con soporte para:
 * - Título y descripción
 * - Contenido personalizado (children)
 * - Botones de acción personalizados o por defecto
 * - Dark mode
 * - Transiciones y accesibilidad (basado en Headless UI Dialog)
 *
 * 🚨 NOTA IMPORTANTE:
 * El prop `inline={true}` es ESTRICTAMENTE PARA DOCUMENTACIÓN EN STORYBOOK.
 * Nunca utilice `inline` en aplicaciones de producción ya que destruye el bloqueo de foco (focus lock)
 * y la gestión de portales, causando problemas severos de accesibilidad.
 *
 * @example
 * ```tsx
 * <AlertDialog
 *   isOpen={isOpen}
 *   title="Are you sure you want to refund this payment?"
 *   description="The refund will be reflected in 2 to 3 business days."
 *   onCancel={() => setIsOpen(false)}
 *   onConfirm={() => console.log('Confirmed')}
 *   confirmText="Refund"
 * />
 * ```
 */
export const AlertDialog = forwardRef<HTMLDivElement, AlertDialogProps>(
  (
    {
      title,
      description,
      isOpen = true, // Defaulting to true for backward compatibility
      inline = false, // Renderiza sin React Portal para previsualizaciones (Storybook)
      icon,
      align = 'left',
      fullWidthButtons = false,
      fullWidthButtonsDirection = 'vertical',
      hideCancel = false,
      showCloseButton = false,
      actions,
      onCancel,
      onConfirm,
      cancelText = 'Cancelar',
      confirmText = 'Confirmar',
      isProcess = false,
      preventCloseOnOverlayClick = false,
      size = 'max-w-lg', // Por defecto Tailwind hace max-w-lg = 32rem (512px)
      type = 'default',
      className = '',
      ...props
    },
    ref
  ) => {
    // Generar clases para el contenedor de botones
    let buttonsContainerClasses = 'flex flex-wrap items-center justify-end gap-3 mt-2';
    if (fullWidthButtons) {
        if (fullWidthButtonsDirection === 'horizontal') {
            buttonsContainerClasses = 'flex flex-row items-center gap-3 mt-2 w-full [&>button]:flex-1';
        } else {
            buttonsContainerClasses = 'flex flex-col-reverse gap-3 mt-2 w-full';
        }
    }

    // Si se proveen actions personalizadas, usarlas. Si no, usar botones por defecto
    const actionButtons = actions || (
      <>
        {!hideCancel && onCancel && (
          <Button type="plain" size="base" onClick={onCancel} disabled={isProcess} fullWidth={fullWidthButtons && fullWidthButtonsDirection === 'vertical'}>
            {cancelText}
          </Button>
        )}
        {onConfirm && (
          <Button type="default" size="base" onClick={onConfirm} disabled={isProcess} fullWidth={fullWidthButtons && fullWidthButtonsDirection === 'vertical'}>
            <span className="flex items-center justify-center gap-2 whitespace-nowrap">
              {confirmText}
              {isProcess && (
                <svg
                  className="animate-spin h-4 w-4 text-white shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
            </span>
          </Button>
        )}
      </>
    );

    const handleClose = () => {
        if (!preventCloseOnOverlayClick && onCancel && !isProcess) {
            onCancel();
        }
    };

    // Variaciones basadas en el alineamiento
    const isCentered = align === 'center';

    const getIconByType = () => {
      if (icon) return icon;

      const containerClasses = `flex shrink-0 items-center justify-center rounded-full h-12 w-12`;
      const iconClasses = `h-6 w-6`;

      switch (type) {
        case 'warning':
          return (
            <div className={`${containerClasses} bg-yellow-100 dark:bg-yellow-950`}>
                <ExclamationTriangleIcon className={`${iconClasses} text-yellow-700 dark:text-yellow-300`} aria-hidden="true" />
            </div>
          );
        case 'danger':
          return (
            <div className={`${containerClasses} bg-red-100 dark:bg-red-950`}>
              <ExclamationCircleIcon className={`${iconClasses} text-red-700 dark:text-red-300`} aria-hidden="true" />
            </div>
          );
        case 'success':
          return (
            <div className={`${containerClasses} bg-green-100 dark:bg-green-950`}>
              <CheckIcon className={`${iconClasses} text-lime-700 dark:text-lime-300`} aria-hidden="true" />
            </div>
          );
        case 'info':
          return (
            <div className={`${containerClasses} bg-cyan-100 dark:bg-cyan-950`}>
               <InformationCircleIcon className={`${iconClasses} text-cyan-700 dark:text-cyan-300`} aria-hidden="true" />
            </div>
          );
        default:
          return null;
      }
    };

    // Clases del contenedor principal
    const containerClasses = `
      w-full
      ${size}
      bg-white dark:bg-zinc-900
      border border-zinc-200 dark:border-zinc-600
      rounded-xl
      transform flex-col overflow-hidden text-left align-middle shadow-xl transition-all
      ${className}
    `.trim().replace(/\s+/g, ' ');

    const PanelContent = (
      <div className="p-6 flex flex-col gap-4 relative">
        {/* Close Button X (Optional) */}
        {showCloseButton && (
          <button
            type="button"
            className="absolute top-4 right-4 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:hover:text-gray-300"
            onClick={handleClose}
            disabled={isProcess}
          >
            <span className="sr-only">Cerrar</span>
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <div className={`flex ${isCentered ? 'flex-col items-center text-center' : 'items-start'} gap-4`}>
          {/* Icon (Optional/Generated) */}
          {(icon || type !== 'default') && (
            <div className={`flex-shrink-0 ${isCentered ? 'mx-auto' : 'mt-0.5'}`}>
              {getIconByType()}
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex flex-col gap-4 flex-1 w-full">
            {/* Text Section / Title & Default Description */}
            <div className="flex flex-col gap-3">
              {/* Title */}
              {inline ? (
                <p className={`text-sm leading-5 font-bold ${!isCentered && showCloseButton ? 'pr-6' : ''}`}>
                  {title}
                </p>
              ) : (
                <Dialog.Title as="p" className={`text-sm leading-5 font-bold ${!isCentered && showCloseButton ? 'pr-6' : ''}`}>
                  {title}
                </Dialog.Title>
              )}

              {/* Description (Opcional) */}
              {description && (
                <div className={`text-sm leading-5 font-normal text-content-tertiary dark:text-dark-content-tertiary ${isCentered ? 'text-center' : ''}`}>
                  {description}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className={buttonsContainerClasses}>
          {actionButtons}
        </div>
      </div>
    );

    // Si está en modo 'inline' (Storybook Preview), devuelve un contenedor simple y manejable
    // que se ajustará a su elemento padre perfectamente.
    if (inline) {
      if (!isOpen) return null;
      return (
        <div className={`absolute inset-0 z-50 flex items-center justify-center p-4 sm:p-0 transition-opacity duration-300 pointer-events-auto`}>
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-[2px] transition-opacity cursor-pointer"
            onClick={handleClose}
          />
          <div ref={ref as any} className={`${containerClasses} relative z-10 w-full flex justify-center`} {...props}>
            {PanelContent}
          </div>
        </div>
      );
    }

    return (
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" open={isOpen} className="relative z-50 text-left" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-[2px] transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  ref={ref as any}
                  className={containerClasses}
                  {...props}
                >
                  {PanelContent}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }
);

AlertDialog.displayName = 'AlertDialog';
